/**
 * Selection Tree Component
 *
 * Navigation tree for the mapping workspace.
 * Redesigned with responsive behavior and improved visual hierarchy.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  Tooltip,
  alpha,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ExpandLess,
  ExpandMore,
  Add as AddIcon,
  Settings as SettingsIcon,
  Engineering as EngineeringIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ObjectType } from '../../../../shared/types';
import type { MappingInsightTone, MappingScopeSummary } from '../../lib/mappingWorkspaceModel';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../../shared/ui/common/HelpTooltipTrigger';
import { ConfirmationDialog } from '../../../../shared/ui/dialogs/ConfirmationDialog';
import {
  mappingOverviewTooltips,
  mappingWorkspaceTooltips,
} from '../../../../shared/config/mappingHelpCatalog';
import {
  borderRadius, componentSizes, getControlBorderColor, getModernScrollbarThin, getSemanticSurfaceTokens, layoutGap,
} from '../../../../theme/theme';
import { MappingStatusChip } from '../../../../shared/ui/mapping';
import type { SemanticSurfaceTokens } from '../../../../theme/theme';
import {
  computeSelectableObjectTypes,
  getDeleteObjectTypeMessage,
  getSelectedScopeLabel,
  isNodeSelected,
  type SelectedNode,
} from '../../lib/selectionTreeModel';
import StatusBuilderNavigation from './StatusBuilderNavigation';
import { BodyText, OverlineText } from '../../../../shared/ui/typography';
const shouldLogSelection = false;

interface SelectionTreeSectionSummary {
  label: string;
  tone: MappingInsightTone;
}

interface SelectionTreeWorkspaceSummary {
  unresolved: number;
  overridden: number;
  scopes: number;
}

interface SelectionTreeProps {
  objectTypes: ObjectType[];
  selectedNode: SelectedNode;
  onNodeSelect: (type: string, name?: string) => void;
  onAddObjectType: (objectTypeName: string) => void;
  onDeleteObjectType: (objectTypeName: string) => void;
  generalItemRef?: React.RefObject<HTMLDivElement | null>;
  selectedObjectTypeItemRef?: React.RefObject<HTMLDivElement | null>;
  processConditionsItemRef?: React.RefObject<HTMLDivElement | null>;
  projectPropertiesItemRef?: React.RefObject<HTMLDivElement | null>;
  statusBuilderNavRef?: React.RefObject<HTMLDivElement | null>;
  statusCategoryFirstNavRef?: React.RefObject<HTMLDivElement | null>;
  /** When true, show only icons with tooltips (collapsed sidebar mode) */
  collapsed?: boolean;
  scopeSummaries?: Record<string, MappingScopeSummary>;
  sectionSummaries?: Partial<
    Record<'general' | 'processConditions' | 'projectProperties' | 'advanced', SelectionTreeSectionSummary>
  >;
  workspaceSummary?: SelectionTreeWorkspaceSummary;
  /** Optional callback when mobile menu toggle is clicked */
  onMobileMenuToggle?: () => void;
  /** Whether the mobile menu is currently open */
  mobileMenuOpen?: boolean;
  /** When true, hides the mobile header (used when rendered inside a drawer) */
  hideMobileHeader?: boolean;
}

interface TreeItemProps {
  icon: React.ElementType;
  label: string;
  type: string;
  name?: string;
  itemRef?: React.RefObject<HTMLDivElement | null>;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  level?: number;
  badge?: React.ReactNode;
  selected: boolean;
  isMobile: boolean;
  collapsed: boolean;
  theme: Theme;
  colors: SemanticSurfaceTokens;
  handleNodeSelect: (type: string, name?: string) => void;
  'data-testid'?: string;
}

// Tree item component with responsive behavior
const TreeItem = React.memo(function TreeItemComponent({
  icon: Icon,
  label,
  type,
  name,
  itemRef,
  hasChildren = false,
  isExpanded = false,
  onToggle,
  level = 0,
  badge,
  selected,
  isMobile,
  collapsed,
  theme,
  colors,
  handleNodeSelect,
  'data-testid': dataTestId,
}: TreeItemProps) {
  const handleSelect = () => {
    if (shouldLogSelection) {
      console.info('[MappingSelectionTree] Tree item selected', {
        label,
        type,
        name: name || null,
        collapsed,
        isMobile,
        hasChildren,
        isExpanded,
      });
    }
    handleNodeSelect(type, name);
  };

  // Collapsed mode: icon only with tooltip
  if (collapsed && !isMobile) {
    return (
        <Tooltip title={label} placement="right" arrow>
          <ListItemButton
            ref={itemRef}
            data-testid={dataTestId}
            onClick={handleSelect}
            sx={{
            py: 0.25,
            px: 0.25,
            mx: 'auto',
            mb: layoutGap.micro,
            width: 36,
            minWidth: 36,
            height: 36,
            borderRadius: `${borderRadius.md}px`,
            backgroundColor: selected ? colors.background.selected : 'transparent',
            justifyContent: 'center',
            '&:hover': { backgroundColor: colors.background.hover },
            transition: theme.transitions.create('background-color'),
          }}
        >
          <ListItemIcon
            sx={{
              color: selected ? theme.palette.primary.main : colors.text.tertiary,
              minWidth: 'auto',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: componentSizes.icon }} />
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );
  }

  // Full mode with text
  return (
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          ref={itemRef}
          data-testid={dataTestId}
          onClick={handleSelect}
          sx={{
          py: isMobile ? 0.5 : 0.25,
          pl: isMobile
            ? 1 + level * 0.75
            : 1.25 + level * 1,
          pr: 0.5,
          borderRadius: `${borderRadius.sm}px`,
          mx: 0.25,
          mb: 0.125,
          backgroundColor: selected ? colors.background.selected : 'transparent',
          borderLeft: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
          '&:hover': { backgroundColor: colors.background.hover },
          transition: theme.transitions.create('background-color'),
        }}
      >
        <ListItemIcon
          sx={{
            color: selected ? theme.palette.primary.main : colors.text.secondary,
            minWidth: isMobile ? 32 : componentSizes.treeIcon + 4,
          }}
        >
          <Icon sx={{ fontSize: isMobile ? 18 : componentSizes.icon }} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.25,
              }}
            >
              <BodyText
                secondary
                sx={{
                  color: selected ? colors.text.primary : colors.text.secondary,
                }}
              >
                {label}
              </BodyText>
              {badge}
            </Box>
          }
        />
        {hasChildren && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: colors.text.secondary,
              cursor: 'pointer',
              p: layoutGap.micro,
              borderRadius: `${borderRadius.sm}px`,
              '&:hover': {
                backgroundColor: colors.background.hover,
              },
            }}
          >
            {isExpanded ? (
              <ExpandLess sx={{ fontSize: isMobile ? 22 : '0.875rem' }} />
            ) : (
              <ExpandMore sx={{ fontSize: isMobile ? 22 : '0.875rem' }} />
            )}
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
});

const SelectionTree: React.FC<SelectionTreeProps> = ({
  objectTypes,
  selectedNode,
  onNodeSelect,
  onAddObjectType,
  onDeleteObjectType,
  generalItemRef,
  selectedObjectTypeItemRef,
  processConditionsItemRef,
  projectPropertiesItemRef,
  statusBuilderNavRef,
  statusCategoryFirstNavRef,
  collapsed = false,
  scopeSummaries = {},
  sectionSummaries = {},
  workspaceSummary,
  onMobileMenuToggle,
  mobileMenuOpen = false,
  hideMobileHeader = false,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    components: true,
    advanced: false,
    statusBuilder: false,
  });

  const [addObjectTypeDialogOpen, setAddObjectTypeDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [objectTypeToDelete, setObjectTypeToDelete] = useState<string | null>(null);
  const currentScopeLabel = getSelectedScopeLabel(selectedNode);

  useEffect(() => {
    if (selectedNode.type !== 'statusBuilder' && selectedNode.type !== 'statusCategory') {
      return;
    }

    setOpenSections((prev) => ({
      ...prev,
      advanced: true,
      statusBuilder: true,
    }));
  }, [selectedNode.type]);

  // Force-close sections when collapsed
  const effectiveOpenSections = useMemo(() =>
    collapsed ? {} : openSections,
    [collapsed, openSections]
  );

  const selectableObjectTypes = useMemo(
    () => computeSelectableObjectTypes(objectTypes),
    [objectTypes]
  );

  const componentsTargetName = useMemo(() => {
    const allType = objectTypes.find(
      (objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL'
    )?.name;

    return allType || objectTypes[0]?.name;
  }, [objectTypes]);

  const handleAddObjectTypeClick = useCallback(() => setAddObjectTypeDialogOpen(true), []);
  const handleObjectTypeSelect = useCallback((objectTypeName: string) => {
    onAddObjectType(objectTypeName);
    setAddObjectTypeDialogOpen(false);
  }, [onAddObjectType]);
  const handleCloseDialog = useCallback(() => setAddObjectTypeDialogOpen(false), []);
  const handleDeleteObjectType = useCallback((objectTypeName: string) => {
    setObjectTypeToDelete(objectTypeName);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteObjectType = useCallback(() => {
    if (objectTypeToDelete) {
      onDeleteObjectType(objectTypeToDelete);
    }
    setObjectTypeToDelete(null);
    setDeleteConfirmOpen(false);
  }, [objectTypeToDelete, onDeleteObjectType]);

  const handleCancelDeleteObjectType = useCallback(() => {
    setObjectTypeToDelete(null);
    setDeleteConfirmOpen(false);
  }, []);
  const handleToggleSection = useCallback((section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const isSelected = useCallback(
    (type: string, name?: string) => isNodeSelected(selectedNode, type, name),
    [selectedNode]
  );

  const handleNodeSelect = useCallback((type: string, name?: string) => {
    if (shouldLogSelection) {
      console.info('[MappingSelectionTree] Forwarding selection', {
        type,
        name: name || null,
        previousType: selectedNode.type,
        previousName: selectedNode.name || null,
        isMobile,
        willCloseMobileMenu: Boolean(isMobile && onMobileMenuToggle),
      });
    }

    if (type === 'components' && componentsTargetName) {
      setOpenSections((prev) => ({ ...prev, components: true }));
      onNodeSelect('objectType', componentsTargetName);
    } else {
      onNodeSelect(type, name);
    }

    // On mobile, close the menu after selection
    if (isMobile && onMobileMenuToggle) {
      onMobileMenuToggle();
    }
  }, [componentsTargetName, onNodeSelect, isMobile, onMobileMenuToggle, selectedNode.name, selectedNode.type]);

  const renderStatusChip = useCallback((summary?: SelectionTreeSectionSummary) => {
    if (!summary || collapsed || summary.tone === 'default') {
      return null;
    }

    return (
      <MappingStatusChip
        tone={summary.tone}
        label={summary.label}
        size="compact"
        dataTestId="selection-tree-status-chip"
      />
    );
  }, [collapsed]);

  // Mobile header with menu toggle
  const MobileHeader = useCallback(() => {
    if (!isMobile || hideMobileHeader) return null;
    return (
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <OverlineText sx={{ color: colors.text.tertiary }}>Mapping scope</OverlineText>
          <BodyText
            sx={{
              mt: 0.25,
              color: colors.text.primary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {currentScopeLabel}
          </BodyText>
        </Box>
        {onMobileMenuToggle && (
          <IconButton
            onClick={onMobileMenuToggle}
            size="small"
            aria-label={mobileMenuOpen ? 'Close mapping scope navigator' : 'Open mapping scope navigator'}
            sx={{
              color: colors.text.secondary,
              backgroundColor: colors.background.panel,
              '&:hover': {
                backgroundColor: colors.background.hover,
              },
            }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        )}
      </Box>
    );
  }, [isMobile, hideMobileHeader, colors, currentScopeLabel, onMobileMenuToggle, mobileMenuOpen]);

  // Desktop header
  const DesktopHeader = useCallback(() => {
    if (collapsed || isMobile) return null;
    return (
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
        }}
      >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row }}>
              <OverlineText sx={{ color: colors.text.tertiary }}>Mapping scope</OverlineText>
              <HelpTooltipTrigger
                label="Mapping scope"
                help={mappingWorkspaceTooltips.currentScopeRail}
                placement="bottom-start"
              />
            </Box>
            {workspaceSummary && workspaceSummary.unresolved > 0 && (
              <MappingStatusChip
                tone="warning"
                label={`${workspaceSummary.unresolved} open`}
                size="compact"
                dataTestId="selection-tree-unresolved-chip"
              />
            )}
        </Box>
        <BodyText
          sx={{
            mt: 0.25,
            color: colors.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {currentScopeLabel}
        </BodyText>
      </Box>
    );
  }, [collapsed, isMobile, colors, currentScopeLabel, workspaceSummary]);

  // Tree content - memoized to prevent unnecessary re-renders
  const treeContent = useMemo(() => {
    const treeItemProps = {
      isMobile,
      collapsed,
      theme,
      colors,
      handleNodeSelect,
    };

    return (
      <List
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 0.25,
          ...getModernScrollbarThin(theme, "divider"),
        }}
      >
        {/* General Section */}
        <TreeItem
          {...treeItemProps}
          itemRef={generalItemRef}
          data-testid="mapping-tour-target-navigator-general"
          icon={DashboardIcon}
          label="Shared PCF attributes"
          type="general"
          selected={isSelected('general')}
          badge={renderStatusChip(sectionSummaries.general)}
        />

        {/* Components Section */}
        <TreeItem
          {...treeItemProps}
          icon={effectiveOpenSections.components ? FolderOpenIcon : FolderIcon}
          label="Object type mappings"
          type="components"
          hasChildren={!collapsed}
          isExpanded={effectiveOpenSections.components}
          onToggle={collapsed ? undefined : () => handleToggleSection('components')}
          selected={isSelected('components')}
        />

        <Collapse in={effectiveOpenSections.components} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ mt: 0.125 }}>
            {/* Add New Object Type */}
            <ListItem disablePadding>
              <ActionHelpTooltip help={mappingOverviewTooltips.selectionTreeAddObjectType} placement="right">
                <ListItemButton
                  onClick={handleAddObjectTypeClick}
                  sx={{
                    pl: isMobile ? 1.5 : 2,
                    py: isMobile ? 0.5 : 0.25,
                    mx: 0.25,
                    mb: 0.125,
                    borderRadius: `${borderRadius.sm}px`,
                    border: `1px dashed ${colors.border.light}`,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: colors.background.hover,
                      borderColor: colors.border.default,
                      borderStyle: 'solid',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isMobile ? 32 : componentSizes.treeIcon,
                      color: colors.text.tertiary,
                    }}
                  >
                    <AddIcon sx={{ fontSize: isMobile ? 18 : '0.6875rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <BodyText secondary sx={{ color: colors.text.tertiary }}>
                        Add object type scope
                      </BodyText>
                    }
                  />
                </ListItemButton>
              </ActionHelpTooltip>
            </ListItem>

            {/* Object Types */}
            {objectTypes.map((objType) => {
              const scopeSummary = scopeSummaries[objType.name];
              const objectTypeSelected = selectedNode.type === 'objectType' && selectedNode.name === objType.name;
              const showCount = !!scopeSummary && (objectTypeSelected || scopeSummary.requiredOpen > 0);

              return (
                <ListItem key={objType.name} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    ref={objectTypeSelected ? selectedObjectTypeItemRef : undefined}
                    data-testid={objectTypeSelected ? 'mapping-tour-target-selected-object-type-nav' : undefined}
                    selected={objectTypeSelected}
                    onClick={() => handleNodeSelect('objectType', objType.name)}
                    sx={{
                      pl: isMobile ? 1.5 : 2,
                      pr: 0.25,
                      py: isMobile ? 0.5 : 0.25,
                      mx: 0.25,
                      mb: 0.125,
                      borderRadius: `${borderRadius.sm}px`,
                      backgroundColor: objectTypeSelected ? colors.background.selected : 'transparent',
                      borderLeft: objectTypeSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : '2px solid transparent',
                      '&:hover': { backgroundColor: colors.background.hover },
                      '&.Mui-selected': { backgroundColor: colors.background.selected },
                      '& .scope-delete': {
                        opacity: 0,
                        pointerEvents: 'none',
                      },
                      '&:hover .scope-delete, &.Mui-selected .scope-delete': {
                        opacity: 1,
                        pointerEvents: 'auto',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: layoutGap.dense,
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <BodyText
                        secondary
                        noWrap
                        title={objType.name}
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          color: objectTypeSelected ? colors.text.primary : colors.text.secondary,
                        }}
                      >
                        {objType.name}
                      </BodyText>
                      {showCount && (
                        <MappingStatusChip
                          tone={scopeSummary.requiredOpen > 0 ? 'warning' : 'success'}
                          label={
                            scopeSummary.requiredOpen > 0
                              ? `${scopeSummary.requiredOpen} open`
                              : `${scopeSummary.mapped}/${scopeSummary.total}`
                          }
                          size="compact"
                          dataTestId="selection-tree-scope-chip"
                        />
                      )}
                      {objType.name !== 'ALL' && (
                        <ActionHelpTooltip help={mappingOverviewTooltips.selectionTreeDeleteObjectType} placement="right">
                          <IconButton
                            className="scope-delete"
                            size="small"
                            aria-label={`Delete ${objType.name} object type mapping scope`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteObjectType(objType.name);
                            }}
                            sx={{
                              ml: 0.125,
                              width: componentSizes.iconButtonSm,
                              height: componentSizes.iconButtonSm,
                              flexShrink: 0,
                              color: colors.text.tertiary,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                color: theme.palette.error.main,
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '0.6875rem' }} />
                          </IconButton>
                        </ActionHelpTooltip>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>

        {/* Process Conditions */}
        <TreeItem
          {...treeItemProps}
          itemRef={processConditionsItemRef}
          data-testid="mapping-tour-target-navigator-process-conditions"
          icon={EngineeringIcon}
          label="Process conditions"
          type="processConditions"
          selected={isSelected('processConditions')}
          badge={renderStatusChip(sectionSummaries.processConditions)}
        />

        {/* Project Properties */}
        <TreeItem
          {...treeItemProps}
          itemRef={projectPropertiesItemRef}
          data-testid="mapping-tour-target-navigator-project-parameters"
          icon={SettingsIcon}
          label="Project properties"
          type="projectProperties"
          selected={isSelected('projectProperties')}
          badge={renderStatusChip(sectionSummaries.projectProperties)}
        />

        {/* Advanced Settings */}
        <TreeItem
          {...treeItemProps}
          icon={BuildIcon}
          label="Advanced settings"
          type="advanced"
          hasChildren={!collapsed}
          isExpanded={effectiveOpenSections.advanced}
          onToggle={collapsed ? undefined : () => handleToggleSection('advanced')}
          selected={isSelected('advanced')}
          badge={renderStatusChip(sectionSummaries.advanced)}
        />

        <Collapse in={effectiveOpenSections.advanced} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ mt: 0.125 }}>
            <StatusBuilderNavigation
              selectedNode={selectedNode}
              collapsed={collapsed}
              isMobile={isMobile}
              isExpanded={Boolean(effectiveOpenSections.statusBuilder)}
              onToggle={() => handleToggleSection('statusBuilder')}
              onNodeSelect={handleNodeSelect}
              statusBuilderNavRef={statusBuilderNavRef}
              statusCategoryFirstNavRef={statusCategoryFirstNavRef}
            />
          </List>
        </Collapse>
      </List>
    );
  }, [
    isMobile,
    collapsed,
    theme,
    colors,
    handleNodeSelect,
    isSelected,
    renderStatusChip,
    sectionSummaries,
    effectiveOpenSections,
    handleToggleSection,
    handleAddObjectTypeClick,
    objectTypes,
    scopeSummaries,
    selectedNode,
    handleDeleteObjectType,
    generalItemRef,
    selectedObjectTypeItemRef,
    processConditionsItemRef,
    projectPropertiesItemRef,
    statusBuilderNavRef,
    statusCategoryFirstNavRef,
  ]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <MobileHeader />
      <DesktopHeader />
      {treeContent}

      {/* Dialog for adding new object type */}
      <Dialog
        open={addObjectTypeDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : `${borderRadius.lg}px`,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: isMobile ? '1rem' : '0.875rem',
            fontWeight: 600,
            px: isMobile ? 1 : 1.5,
            py: isMobile ? 1 : 0.75,
          }}
        >
          Add object type mapping scope
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 1 : 1.5 }}>
          <BodyText
            secondary
            sx={{ mb: 0.5, color: colors.text.secondary }}
          >
            Select the PCF object type that will own these mappings.
          </BodyText>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.25,
              maxHeight: isMobile ? 'calc(100vh - 200px)' : 300,
              overflow: 'auto',
            }}
          >
            {selectableObjectTypes.map((name) => (
              <Button
                key={name}
                variant="outlined"
                size={isMobile ? "medium" : "small"}
                onClick={() => handleObjectTypeSelect(name)}
                sx={{
                  fontSize: isMobile ? '0.75rem' : '0.6875rem',
                  borderColor: getControlBorderColor(theme),
                  color: colors.text.secondary,
                  py: isMobile ? 0.5 : 0.25,
                  px: isMobile ? 1 : 0.5,
                  '&:hover': {
                    borderColor: getControlBorderColor(theme),
                    backgroundColor: colors.background.hover,
                  },
                }}
              >
                {name}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 1 : 1.5, py: 1 }}>
          <Button
            onClick={handleCloseDialog}
            size={isMobile ? "large" : "small"}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={deleteConfirmOpen}
        title="Delete object type mapping scope"
        message={
          objectTypeToDelete ? getDeleteObjectTypeMessage(objectTypeToDelete) : ''
        }
        cancelLabel="Cancel"
        confirmLabel="Delete"
        destructive
        onCancel={handleCancelDeleteObjectType}
        onConfirm={handleConfirmDeleteObjectType}
      />
    </Box>
  );
};

export default React.memo(SelectionTree);
